import { Schema } from "effect";

export const TrimmedString = Schema.Trim;
export const TrimmedNonEmptyString = TrimmedString.check(Schema.isNonEmpty());

export const NonNegativeInt = Schema.Int.check(Schema.isGreaterThanOrEqualTo(0));
export const PositiveInt = Schema.Int.check(Schema.isGreaterThanOrEqualTo(1));

export const IsoDateTime = Schema.String;
export type IsoDateTime = typeof IsoDateTime.Type;

/**
 * Construct a branded identifier. Enforces non-empty trimmed strings
 */
const makeEntityId = <Brand extends string>(brand: Brand) =>
  TrimmedNonEmptyString.pipe(Schema.brand(brand));

export const ThreadId = makeEntityId("ThreadId");
export type ThreadId = typeof ThreadId.Type;
export const ProjectId = makeEntityId("ProjectId");
export type ProjectId = typeof ProjectId.Type;
export const UserId = makeEntityId("UserId");
export type UserId = typeof UserId.Type;
export const TeamId = makeEntityId("TeamId");
export type TeamId = typeof TeamId.Type;
/**
 * Identifier of a persistent project environment — the long-lived worktree,
 * preview and sandbox database owned by one (user, project) pair.
 *
 * Environments exist so that the worktree, the preview URL and the test data of
 * a non-technical user survive their conversations: the owner of those
 * resources is the environment, never the thread that happens to use it.
 */
export const EnvironmentId = makeEntityId("EnvironmentId");
export type EnvironmentId = typeof EnvironmentId.Type;
/**
 * Identifier of a *managed* agent — the user-defined agent configured in
 * Settings (name, provider+model, instructions, tool allowlist, subagents).
 *
 * Deliberately not called `AgentId`: `@taskin-code/shared/agents` already owns
 * that name for the terminal launcher catalog (`claude`, `codex`, ... — CLI
 * binaries spawned into a PTY), which is a different concept with a different
 * id space. Both names would end up in scope together in server and web files,
 * so the managed entity carries the longer prefix everywhere.
 */
/**
 * Identifier of an isolated preview of one pull request.
 *
 * Its own id space rather than `(projectId, prNumber)`, even though that pair
 * identifies the *live* preview: previews are destroyed and re-created for the
 * same pull request, and the rows of the ones that are gone stay as history. A
 * key that repeated would make "which preview did the agent comment about?"
 * unanswerable the second time somebody asks for PR 131.
 */
export const PrPreviewId = makeEntityId("PrPreviewId");
export type PrPreviewId = typeof PrPreviewId.Type;
export const ManagedAgentId = makeEntityId("ManagedAgentId");
export type ManagedAgentId = typeof ManagedAgentId.Type;
/**
 * Identifier of one agent memory — a standing instruction about how to act in a
 * given situation, stored by Taskin rather than by a provider's own memory.
 *
 * Its own id space rather than a composite of (scope, project, user, slug):
 * a memory is edited, approved and read by id, and its slug is a human-facing
 * handle that a rename must be able to change without orphaning the reads that
 * already pointed at it.
 */
export const AgentMemoryId = makeEntityId("AgentMemoryId");
export type AgentMemoryId = typeof AgentMemoryId.Type;
/**
 * Identifier of one tag — a label an admin defines once and then attaches to
 * things of different kinds.
 *
 * Its own id space, and never the `namespace:slug` pair, for the reason
 * `AgentMemoryId` gives: the pair is the human-facing handle, and renaming a
 * tag must not orphan every assignment that already pointed at it.
 */
export const TagId = makeEntityId("TagId");
export type TagId = typeof TagId.Type;
export const CommandId = makeEntityId("CommandId");
export type CommandId = typeof CommandId.Type;
export const EventId = makeEntityId("EventId");
export type EventId = typeof EventId.Type;
export const MessageId = makeEntityId("MessageId");
export type MessageId = typeof MessageId.Type;
export const TurnId = makeEntityId("TurnId");
export type TurnId = typeof TurnId.Type;

export const ProviderItemId = makeEntityId("ProviderItemId");
export type ProviderItemId = typeof ProviderItemId.Type;
export const RuntimeSessionId = makeEntityId("RuntimeSessionId");
export type RuntimeSessionId = typeof RuntimeSessionId.Type;
export const RuntimeItemId = makeEntityId("RuntimeItemId");
export type RuntimeItemId = typeof RuntimeItemId.Type;
export const RuntimeRequestId = makeEntityId("RuntimeRequestId");
export type RuntimeRequestId = typeof RuntimeRequestId.Type;
export const RuntimeTaskId = makeEntityId("RuntimeTaskId");
export type RuntimeTaskId = typeof RuntimeTaskId.Type;
export const ApprovalRequestId = makeEntityId("ApprovalRequestId");
export type ApprovalRequestId = typeof ApprovalRequestId.Type;
export const CheckpointRef = makeEntityId("CheckpointRef");
export type CheckpointRef = typeof CheckpointRef.Type;
